import React from "react";
import {
	calculateTotalsByCurrency,
	CurrencyTotals,
} from "../sammelrechnungen-table.utils";
import { RecipientTypeEnum } from "../types";
import { CompletedJobDocumentType } from "@/lib/supabase/supabaseTypes";
import { applyRounding } from "../../../lib/utils/calculate";
import { RoundToType } from "../../../store/Jobs/utils";

interface TotalsFooterProps {
	recipientGroups: Array<{
		recipientType: RecipientTypeEnum;
		recipientId: string;
		clientId: string;
		jobDocuments: CompletedJobDocumentType[];
	}>;
	selectedJobDocumentIds: Record<string, Set<number>>;
	akontoLookup: Record<string, number>;
	akontoEnabled: Record<string, boolean>;
}

const formatCurrency = (value: number): string => {
	const isNegative = value < 0;
	const absoluteValue = Math.abs(value);
	const [integerPart, decimalPart] = absoluteValue.toFixed(2).split(".");
	const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
	return `${isNegative ? "-" : ""}${formattedInteger}.${decimalPart}`;
};

const formatAmount = (value: number, currency: string): string =>
	`${formatCurrency(
		applyRounding(value, RoundToType.TO_SECOND_DECIMAL)
	)} ${currency}`;

export const TotalsFooter: React.FC<TotalsFooterProps> = ({
	recipientGroups,
	selectedJobDocumentIds,
	akontoLookup,
	akontoEnabled,
}) => {
	const breakdowns = calculateTotalsByCurrency(
		recipientGroups,
		selectedJobDocumentIds,
		akontoLookup,
		akontoEnabled
	);

	const totalBreakdowns = breakdowns.filter((b) => b.totalJobCount > 0);
	const selectedBreakdowns = breakdowns.filter((b) => b.selectedJobCount > 0);

	return (
		<div className="sticky bottom-0 bg-white border-t shadow-lg z-10 mt-auto">
			<div className="px-6 py-4">
				<div className="flex items-center justify-between gap-8 max-w-screen-2xl mx-auto">
					<div className="flex items-center gap-8">
						<div className="flex flex-col gap-3">
							<span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
								Gesamt
							</span>
							{totalBreakdowns.map((b) => (
								<TotalLine key={b.currency} breakdown={b} />
							))}
						</div>

						<div className="h-12 w-px bg-gray-300" />

						<div className="flex flex-col gap-3">
							<span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
								Ausgewählt
							</span>
							{selectedBreakdowns.length > 0 ? (
								selectedBreakdowns.map((b) => (
									<SelectedLine
										key={b.currency}
										breakdown={b}
									/>
								))
							) : (
								<span className="text-sm text-gray-400 italic">
									Keine Aufträge ausgewählt
								</span>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

const TotalLine: React.FC<{ breakdown: CurrencyTotals }> = ({ breakdown }) => (
	<div className="flex flex-col">
		<div className="flex items-baseline gap-3">
			<span className="text-sm text-gray-700">
				{breakdown.totalJobCount}{" "}
				{breakdown.totalJobCount === 1 ? "Auftrag" : "Aufträge"}
			</span>
			<span className="text-lg font-mono font-semibold text-gray-900">
				{formatAmount(breakdown.totalGrossAmount, breakdown.currency)}
			</span>
		</div>
		<div className="flex items-baseline gap-2 mt-1">
			<span className="text-xs text-gray-500">davon MwSt.</span>
			<span className="text-sm font-mono text-gray-600">
				{formatAmount(breakdown.totalVatAmount, breakdown.currency)}
			</span>
		</div>
	</div>
);

const SelectedLine: React.FC<{ breakdown: CurrencyTotals }> = ({
	breakdown,
}) => (
	<div className="flex flex-col">
		<div className="flex items-baseline gap-3">
			<span className="text-sm text-gray-700">
				{breakdown.selectedJobCount}{" "}
				{breakdown.selectedJobCount === 1 ? "Auftrag" : "Aufträge"}
			</span>
			<span className="text-lg font-mono font-semibold text-blue-600">
				{formatAmount(breakdown.selectedNetAmount, breakdown.currency)}
			</span>
		</div>
		<div className="flex items-baseline gap-2 mt-1">
			<span className="text-xs text-gray-500">davon MwSt.</span>
			<span className="text-sm font-mono text-gray-600">
				{formatAmount(breakdown.selectedVatAmount, breakdown.currency)}
			</span>
		</div>
		{breakdown.selectedAkontoDeduction > 0 && (
			<div className="flex items-baseline gap-2 mt-1">
				<span className="text-xs text-gray-500">Akonto abgezogen</span>
				<span className="text-sm font-mono text-orange-600">
					{formatAmount(
						breakdown.selectedAkontoDeduction,
						breakdown.currency
					)}
				</span>
			</div>
		)}
	</div>
);
